// Project workflow API — maps to the backend `/project` router.
// crawl → generate-tests → run-all → runs. All calls go through the shared
// `api` client (Bearer token + 401 handling + centralized errors).

import { api, filenameFromDisposition, getApiBase, getToken, qs } from "./client";

export const getProjects = () => api.get("/project/");

export const getProject = (id) => api.get(`/project/${id}`);

export const createProject = (data) => api.post("/project/", data);

export const deleteProject = (id) => api.del(`/project/${id}`);

// Partial edit. A project manager can change name/description/base URL; an
// assigned TESTER may set repository_url and nothing else (the server enforces
// that split — the tester is the one who knows which repo backs the app).
export const updateProject = (id, data) => api.patch(`/project/${id}`, data);

// --- Private-repo access token (write-only) --------------------------------
// The token is Fernet-encrypted server-side and never returned; responses only
// report `has_repository_token`. Pass an empty string to clear a stored token.
export const setRepositoryToken = (id, repositoryToken) =>
  api.put(`/project/${id}/repository-token`, { repository_token: repositoryToken });

// Test that a (possibly private) repo is reachable with a token BEFORE saving.
// Pass repositoryUrl/repositoryToken to test un-saved values; omit them to test
// what's already stored on the project. Returns { ok, message }.
export const testRepoAccess = (id, { repositoryUrl, repositoryToken } = {}) =>
  api.post(`/project/${id}/test-repo-access`, {
    ...(repositoryUrl ? { repository_url: repositoryUrl } : {}),
    ...(repositoryToken ? { repository_token: repositoryToken } : {}),
  });

// Project-less preflight test (for the New Project modal, before a project id
// exists). Stores nothing. Returns { ok, message }.
export const testRepoAccessPreflight = ({ repositoryUrl, repositoryToken } = {}) =>
  api.post(`/project/test-repo-access`, {
    ...(repositoryUrl ? { repository_url: repositoryUrl } : {}),
    ...(repositoryToken ? { repository_token: repositoryToken } : {}),
  });

// --- Multiple repositories per project -------------------------------------
// Frontend and backend often live in separate repos under separate accounts, so
// each row carries its OWN token and is tested on its own — a dead backend token
// must not report the whole connection as broken. Tokens are write-only
// throughout: rows report `has_token`, never the value.

export const getProjectRepositories = (id) => api.get(`/project/${id}/repositories`);

export const addProjectRepository = (id, { repositoryUrl, label, kind, isPrivate, repositoryToken } = {}) =>
  api.post(`/project/${id}/repositories`, {
    repository_url: repositoryUrl,
    ...(label ? { label } : {}),
    ...(kind ? { kind } : {}),
    is_private: !!isPrivate,
    ...(repositoryToken ? { repository_token: repositoryToken } : {}),
  });

// Partial edit. The token is deliberately NOT accepted here — it travels on
// setProjectRepositoryToken, so a metadata edit can't blank a stored secret.
export const updateProjectRepository = (id, repoId, data) =>
  api.patch(`/project/${id}/repositories/${repoId}`, data);

// Pass an empty string to clear this repository's stored token.
export const setProjectRepositoryToken = (id, repoId, repositoryToken) =>
  api.put(`/project/${id}/repositories/${repoId}/token`, { repository_token: repositoryToken });

// Check ONE repository with its own token. Pass repositoryUrl/repositoryToken to
// test un-saved values. Returns { ok, message, repository }.
export const testProjectRepositoryAccess = (id, repoId, { repositoryUrl, repositoryToken } = {}) =>
  api.post(`/project/${id}/repositories/${repoId}/test-access`, {
    ...(repositoryUrl ? { repository_url: repositoryUrl } : {}),
    ...(repositoryToken ? { repository_token: repositoryToken } : {}),
  });

export const deleteProjectRepository = (id, repoId) =>
  api.del(`/project/${id}/repositories/${repoId}`);

// Step 1 — discover pages. Returns { status:"crawling", poll_url } and runs in
// the background; poll getPages() until pages_found > 0.
export const crawlProject = (id) => api.post(`/project/${id}/crawl`);

export const getPages = (id) => api.get(`/project/${id}/pages`);

// Step 2 — kick off the whole crawl→generate workflow in one background task.
// Returns { status:"generating", poll_url:"…/workflow" } (or { status:"skipped" }
// when cases already exist and regenerate wasn't requested). Poll getWorkflow()
// until state is "completed" or "failed".
// `account` (a credential label) generates for ONE login only: it crawls just
// that account, replaces only its test cases, and leaves the other accounts'
// suites untouched — so adding a role later doesn't re-run the whole project.
export const generateTests = (id, { regenerate = false, account } = {}) =>
  api.post(`/project/${id}/generate-tests`, null, {
    params: { regenerate, ...(account ? { account } : {}) },
  });

// What a previous crawl discovered and never reached, grouped by login.
// { total, accounts: [{ label, count, urls }] }. Drives the "Generate queued
// pages" button — total of 0 hides it.
export const getQueuedPages = (id) => api.get(`/project/${id}/queued-pages`);

// Generate test cases for the QUEUED pages only. A normal regenerate starts at
// the base URL, rediscovers every link and pays the model for every page again;
// this crawls exactly what is queued and adds its cases to the current version,
// leaving the existing suite alone. Poll getWorkflow() as usual.
export const generateQueuedPages = (id, { account } = {}) =>
  api.post(`/project/${id}/generate-queued`, null, {
    params: { ...(account ? { account } : {}) },
  });

// Generate test cases for a SINGLE new page/tab. Crawls only `url` (logged in
// with the chosen `account` label), then appends its cases to the current suite
// (replacing only that page's cases) — no full re-crawl. Poll getWorkflow()
// until state is "completed"/"failed", same as generateTests.
export const generateTestsForPage = (id, { url, account }) =>
  api.post(`/project/${id}/generate-tests-for-page`, { url, account });

// Unpaged (up to the server's 500 ceiling) — what most screens still use.
export const getProjectTestCases = (id) => api.get(`/project/${id}/test-cases`);
// Server-side page + filters, for projects too big to hold in one response.
// Returns the same shape plus filtered_total / limit / offset.
export const pageProjectTestCases = (id, params = {}) =>
  api.get(`/project/${id}/test-cases${qs(params)}`);

// ONE case, in full: name, preconditions, numbered steps, test data, expected
// result. The list endpoint returns six fields per row because it pages through
// thousands; this is the read-one call behind the case detail view.
export const getProjectTestCase = (projectId, caseId) =>
  api.get(`/project/${projectId}/test-cases/${caseId}`);

// Live status of the project's current crawl+generate / run-all workflow.
// Shape: { kind, phase, state:"idle"|"running"|"completed"|"failed", message,
// error, pages_found?, tests_total? }. Drives the progress banner, resume-on-
// return, and real error reporting (instead of inferring failure from a timeout).
export const getWorkflow = (id) => api.get(`/project/${id}/workflow`);

// Step 3 — queue test cases as runs. Background; poll getProjectRuns() until
// completed === total. By default only cases without an existing run are queued
// (so re-clicking doesn't duplicate the batch); pass rerun to wipe prior runs
// and execute the whole suite again. Returns { status:"skipped", ... } when
// every case already ran and rerun was not requested.
export const runAllTests = (id, { limit, rerun = false, pageUrl } = {}) =>
  api.post(`/project/${id}/run-all`, null, {
    params: {
      ...(limit ? { limit } : {}),
      ...(rerun ? { rerun: true } : {}),
      ...(pageUrl ? { page_url: pageUrl } : {}),
    },
  });

// Run test cases for a single page (convenience wrapper around runAllTests).
export const runPageTests = (id, pageUrl, opts) =>
  runAllTests(id, { ...opts, pageUrl });

export const getProjectRuns = (id) => api.get(`/project/${id}/runs`);

// Stop the in-progress background work for this project (crawl / generate /
// run-all). The flag is checked between units of work and clears itself once
// the task exits — no need to call DELETE /cancel afterwards in normal use.
export const cancelProject = (id) => api.post(`/project/${id}/cancel`);

// Download the project's cumulative report (one row per run). The bytes come
// from an auth-protected endpoint, so a plain <a href> can't send the Bearer
// token — fetch the blob with auth, then trigger a download. format: "pdf" | "xlsx".
async function downloadFile(path, fallbackName) {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const url = new URL(getApiBase() + path, origin);
  const token = getToken();
  const res = await fetch(url.toString(), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const dispo = res.headers.get("Content-Disposition") || "";
  const name = filenameFromDisposition(dispo, fallbackName);
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}

export const downloadProjectReport = (id, format) =>
  downloadFile(`/project/${id}/report.${format}`, `report.${format}`);

// All generated/imported test cases for a project (run-independent), as Excel.
// Optionally scope to one page (`pageUrl`) or one login (`account`) — used by
// the "Test a single page" card's download and the account-wise download.
// `account` accepts a login label OR a sheet key ("public" / "auth"), so the
// non-credential sheets can be downloaded on their own too.
export const downloadProjectTestCases = (id, { pageUrl, account } = {}) => {
  const qs = new URLSearchParams();
  if (pageUrl) qs.set("page_url", pageUrl);
  if (account) qs.set("account", account);
  const suffix = qs.toString() ? `?${qs}` : "";
  return downloadFile(
    `/project/${id}/test-cases.xlsx${suffix}`,
    // Only used if the response carries no Content-Disposition; the server names
    // the file after the project ("oda7 test cases.xlsx").
    "test cases.xlsx",
  );
};

// The tabs the workbook will contain, in order: [{key, name, kind, count,
// pages, modules}]. Drives the pre-download sheet breakdown.
export const getTestCaseSheets = (id) =>
  api.get(`/project/${id}/test-case-sheets`);

export const importProjectTestCases = (id, testCases) =>
  api.post(`/project/${id}/test-cases/import`, testCases);

// --- Test-case version history (Phase 4/5) ---------------------------------
// Non-destructive regeneration keeps every prior generation. List them (newest
// first: { version, count, is_current, created_at }), roll one back to current,
// or download a specific version as Excel.
export const getTestCaseVersions = (id) =>
  api.get(`/project/${id}/test-case-versions`);

export const rollbackTestCases = (id, version) =>
  api.post(`/project/${id}/test-cases/rollback`, { version });

export const downloadTestCasesVersion = (id, version) =>
  downloadFile(
    `/project/${id}/test-cases.xlsx?version=${version}`,
    `Generated_Test_Cases-v${version}.xlsx`,
  );

// Delete a project's CURRENT imported/generated test cases (runs are kept).
// Archived versions survive and stay downloadable; pass { includeArchived: true }
// to purge the version history along with them.
export const clearProjectTestCases = (id, { includeArchived = false } = {}) =>
  api.del(`/project/${id}/test-cases${includeArchived ? "?include_archived=true" : ""}`);

// --- Project credentials (login creds for auth-gated pages) ---------------
// Passwords are write-only: list responses never include them (just has_password).
// Each credential also carries the "Use Custom Username Field" toggle
// (`username_field_override_enabled` + `username_field_override`) so users
// can pin the login identifier to a specific field on the target site
// (e.g. "Email Address") instead of relying on automatic detection.
export const getCredentials = (id) => api.get(`/project/${id}/credentials`);

export const addCredential = (id, data) =>
  api.post(`/project/${id}/credentials`, data);

export const updateCredential = (id, credId, data) =>
  api.put(`/project/${id}/credentials/${credId}`, data);

export const deleteCredential = (id, credId) =>
  api.del(`/project/${id}/credentials/${credId}`);

// Common username-field labels users can pick from in the toggle's quick
// selector. The selector agent resolves whatever the user picks against
// the actual DOM via the same alias engine that powers automatic
// detection — these are just convenient starting points.
export const USERNAME_FIELD_PRESETS = [
  { value: "", label: "Auto-detect (Username → Email → User ID)" },
  { value: "Username", label: "Username" },
  { value: "Email Address", label: "Email Address" },
  { value: "Email", label: "Email" },
  { value: "User ID", label: "User ID" },
  { value: "Login ID", label: "Login ID" },
  { value: "Account ID", label: "Account ID" },
  { value: "Mobile", label: "Mobile" },
  { value: "Phone Number", label: "Phone Number" },
];

// ---- Project staffing -----------------------------------------------------
// Who the project manager has put on this project. Testers run the suite,
// developers fix what it finds; both see the project in their dashboard and
// assigned devs receive the batch-completion report email.
export const getProjectAssignees = (id) => api.get(`/project/${id}/assignees`);
// Takes an ARRAY — assigning a team one person at a time is unusable.
export const assignToProject = (id, userIds) =>
  api.post(`/project/${id}/assignees`, {
    user_ids: Array.isArray(userIds) ? userIds : [userIds],
  });
export const unassignFromProject = (id, userId) =>
  api.del(`/project/${id}/assignees/${userId}`);

// ---- Push test cases into a runner ---------------------------------------
// Copies this project's current test cases into the Exe / Extension catalog,
// TAGGED with the project. That tag is what lets a batch report be traced back
// here, which is what the developer dashboard reads.
// Scope with `pageUrl` (everything on one page) or `caseIds` (just the ones you
// ticked); pass neither to push the whole project. Ids win if both are given.
// They travel in the BODY — a page can hold hundreds, and a query string that
// long gets truncated in transit.
//
// Both return { pushed, duplicates, skipped } — show it. Re-pushing is safe:
// the runner de-duplicates, so "already there" is information, not an error.
const pushBody = ({ pageUrl, caseIds } = {}) => ({
  ...(pageUrl ? { page_url: pageUrl } : {}),
  ...(caseIds && caseIds.length ? { case_ids: caseIds } : {}),
});

export const pushToExe = (id, scope = {}) =>
  api.post(`/project/${id}/push-to-exe`, pushBody(scope));
export const pushToExtension = (id, scope = {}) =>
  api.post(`/project/${id}/push-to-extension`, pushBody(scope));

// ---- Pause / resume / stop a running crawl+generate ----------------------
// Cooperative, like Stop: the page being crawled and any case already
// generating finish, then the workers park. Nothing found so far is lost.
export const pauseProject = (id) => api.post(`/project/${id}/pause`, {});
export const resumeProject = (id) => api.post(`/project/${id}/resume`, {});
